import { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Crown,
  MoreVertical,
  ArrowUpCircle,
  Vote,
  AlertCircle,
  Users,
  X,
  Loader2,
} from 'lucide-react';
import { api, parseApiError, type OrganizationRole, type ScopedOrgMember } from '../lib/api';
import { orgRoleDisplayName } from '../lib/roles';
import { normalizeOrgMembersResponse } from '../lib/orgMembersPaging';
import { Card } from './Card';
import { ConfirmModal } from './ConfirmModal';
import { cn } from '../lib/cn';
import { SETTLE, settleDelay } from '../lib/motion';
import { usePageActions } from '../lib/pageActions';
import { useSectionHighlightContext } from '../lib/sectionHighlight';

// ─────────────────────────────────────────────────────────────────────────────
// OrgOwnerBoardPanel — PR4 governance UI, board-of-owners surface.
//
// The org is a board of up to OWNER_SEAT_CAP (3) peers holding ORG_OWNER.
// METADATA.ownerDiscordId marks the FOUNDING owner — the only owner who may
// add a co-owner or hand the add-power to a different peer. Any owner may
// propose a REMOVE_OWNER vote against another (non-founding) owner; the
// board itself decides via OrgProposalsPanel below.
//
// Backend contract notes (read directly off portalApi/index.ts — do not
// trust prior assumptions):
//   - POST /orgs/:id/members (brand-new member) gates ORG_OWNER assignment
//     via canAssignRole(..., { isParentOwner: undefined }) — ORG has no
//     parent scope, so this path is effectively founder-only and can NEVER
//     mint a co-owner for an ordinary founding owner. "Add co-owner" must
//     therefore PROMOTE an EXISTING member via PATCH
//     /orgs/:id/members/:discordId, which has its own explicit
//     isFoundingOwner() bypass. So the picker below lists current non-owner
//     members, not a fresh Discord-wide search.
//   - Owners can no longer be demoted/removed directly (PR1) — "Propose
//     removal" opens a REMOVE_OWNER governance vote instead (see
//     OrgProposalsPanel), it never mutates the row itself.
// ─────────────────────────────────────────────────────────────────────────────

const OWNER_SEAT_CAP = 3;

function shortId(discordId: string): string {
  return `…${discordId.slice(-6)}`;
}

function roleLabel(role: OrganizationRole): string {
  return orgRoleDisplayName(role);
}

function displayName(m: { discordId: string; gamertag?: string; displayName?: string }): string {
  return m.displayName ?? m.gamertag ?? shortId(m.discordId);
}

// ── OwnerRow — per-row sub-component (unexported) ──────────────────────────────

type OwnerRowProps = {
  member: ScopedOrgMember;
  index: number;
  isSelf: boolean;
  isFoundingRow: boolean;
  canAppoint: boolean; // actor can move the add-power onto this row
  canPropose: boolean; // actor can open a REMOVE_OWNER vote against this row
  onAppointRequest: (m: ScopedOrgMember) => void;
  onProposeRequest: (m: ScopedOrgMember) => void;
};

function OwnerRow({
  member,
  index,
  isSelf,
  isFoundingRow,
  canAppoint,
  canPropose,
  onAppointRequest,
  onProposeRequest,
}: OwnerRowProps) {
  // ── ALL hooks before any early return ─────────────────────────────────────
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const showMenu = canAppoint || canPropose;

  return (
    <div
      className={cn(
        `${SETTLE} group relative flex items-center justify-between border border-iron-800/60 bg-iron-950 px-4 py-3 transition-colors`,
        'hover:border-forge-900/60 hover:bg-iron-950'
      )}
      style={settleDelay(index)}
    >
      <div className="flex min-w-0 items-center gap-3">
        <div
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center border',
            isFoundingRow ? 'border-forge-500/40 bg-forge-500/10' : 'border-iron-700/50 bg-iron-950'
          )}
        >
          <Crown className={cn('h-4 w-4', isFoundingRow ? 'text-forge-300' : 'text-iron-400')} strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <p className="truncate font-display text-sm font-black uppercase tracking-wide text-white">
            {displayName(member)}
            {isSelf && <span className="ml-2 font-mono text-[9px] font-normal text-iron-500">(you)</span>}
          </p>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-iron-500">
            {member.clanName && <span>{member.clanName}</span>}
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {isFoundingRow && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 font-display text-[9px] font-black uppercase tracking-[0.18em] text-forge-300 ring-1 ring-inset ring-forge-500/30">
            Founding
          </span>
        )}

        {showMenu && (
          <div ref={menuRef} className="relative">
            <button
              onClick={() => setMenuOpen(o => !o)}
              className="flex h-7 w-7 items-center justify-center border border-iron-800 text-iron-400 transition-colors hover:border-forge-500 hover:text-forge-200"
              aria-label="Owner actions"
            >
              <MoreVertical className="h-3.5 w-3.5" strokeWidth={2.5} />
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-full z-40 mt-1 w-56 border border-forge-900/60 bg-iron-950 shadow-forged">
                <div className="border-b border-forge-900/40 px-3 py-2">
                  <p className="font-display text-[9px] font-black uppercase tracking-[0.22em] text-forge-400">Actions</p>
                </div>
                <div className="py-1">
                  {canAppoint && (
                    <button
                      onClick={() => { setMenuOpen(false); onAppointRequest(member); }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-iron-200 transition-colors hover:bg-iron-950 hover:text-white"
                    >
                      <Crown className="h-3 w-3 text-forge-400" strokeWidth={2.5} />
                      Appoint as founding owner
                    </button>
                  )}
                  {canPropose && (
                    <button
                      onClick={() => { setMenuOpen(false); onProposeRequest(member); }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-red-300 transition-colors hover:bg-red-950/40 hover:text-red-200"
                    >
                      <Vote className="h-3 w-3" strokeWidth={2.5} />
                      Propose removal
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── OrgOwnerBoardPanel — exported ──────────────────────────────────────────────

export function OrgOwnerBoardPanel({
  orgId,
  currentUserId,
  isFounder,
  currentRole,
  foundingOwnerDiscordId,
}: {
  orgId: string;
  currentUserId: string;
  isFounder: boolean;
  currentRole: OrganizationRole;
  foundingOwnerDiscordId: string;
}) {
  const qc = useQueryClient();

  // ── ALL hooks declared before any early return ────────────────────────────
  const [addSelectedId, setAddSelectedId] = useState('');
  const [addError, setAddError] = useState('');
  const [appointTarget, setAppointTarget] = useState<ScopedOrgMember | null>(null);
  const [appointError, setAppointError] = useState('');
  const [proposeTarget, setProposeTarget] = useState<ScopedOrgMember | null>(null);
  const [proposeError, setProposeError] = useState('');
  const [proposeNotice, setProposeNotice] = useState('');

  const canView = currentRole === 'ORG_OWNER' || isFounder;
  // Actor is CURRENT founding owner by identity, or a platform founder override.
  const actorIsFoundingOwner = isFounder || currentUserId === foundingOwnerDiscordId;
  // Any owner (or founder) may open a REMOVE_OWNER vote.
  const actorCanPropose = isFounder || currentRole === 'ORG_OWNER';

  // Shared cache key with OrgMembersPanel — TanStack dedupes this fetch when
  // both panels are mounted together, so this costs nothing extra. Only
  // page one is ever needed here (owner list is always small), so the
  // envelope's nextCursor/importStatus are simply dropped — this panel just
  // reads .members through the both-shapes-tolerant normalizer.
  const { data: pageOneRaw, isLoading } = useQuery({
    queryKey: ['orgMembers', orgId],
    queryFn: () => api.orgMembers(orgId),
    enabled: canView,
  });
  const members = useMemo(() => normalizeOrgMembersResponse(pageOneRaw).members, [pageOneRaw]);

  const promoteMut = useMutation({
    mutationFn: (discordId: string) => api.updateOrgMemberRole(orgId, discordId, 'ORG_OWNER'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgMembers', orgId] });
      setAddSelectedId('');
      setAddError('');
    },
    onError: (e: Error) => setAddError(parseApiError(e).message),
  });

  const appointMut = useMutation({
    mutationFn: (discordId: string) => api.appointFoundingOwner(orgId, discordId),
    onSuccess: () => {
      // ownerDiscordId lives on org METADATA (['orgs']), not the members list.
      qc.invalidateQueries({ queryKey: ['orgs'] });
      qc.invalidateQueries({ queryKey: ['orgMembers', orgId] });
      setAppointTarget(null);
      setAppointError('');
    },
    onError: (e: Error) => {
      setAppointError(parseApiError(e).message);
      setAppointTarget(null);
    },
  });

  const proposeMut = useMutation({
    mutationFn: (discordId: string) => api.createOrgProposal(orgId, 'REMOVE_OWNER', discordId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgProposals', orgId] });
      setProposeTarget(null);
      setProposeError('');
      setProposeNotice('Vote opened — see it in Governance Proposals below.');
    },
    onError: (e: Error) => {
      const parsed = parseApiError(e);
      setProposeTarget(null);
      setProposeNotice('');
      setProposeError(
        parsed.code === 'proposal_already_open'
          ? `${parsed.message} There's already an open vote for this — check Governance Proposals below.`
          : parsed.message
      );
    },
  });

  // Global bottom action-bar registration — "Promote to owner" is a
  // scroll-to+focus shortcut into the Add-co-owner block above, NOT a
  // duplicate submit: the picker needs an actual selection first, so the
  // bar action just gets you there with the member-select auto-focused (see
  // lib/sectionHighlight's highlightNow, which focuses the first empty
  // input/select inside the target section). Gated on actorIsFoundingOwner
  // — the SAME permission the Add-co-owner block itself gates on (see
  // lib/pageActions.tsx's role-awareness contract). 'org-ownerboard' is
  // registered as a highlight target by TournamentSetup's OrgPane, wrapping
  // this component's mount point.
  const highlight = useSectionHighlightContext();
  const promoteActions = useMemo(
    () => (actorIsFoundingOwner ? [{
      id: 'org-ownerboard-promote',
      label: 'Promote to owner',
      icon: ArrowUpCircle,
      onClick: () => highlight?.highlightNow('org-ownerboard'),
    }] : null),
    [actorIsFoundingOwner, highlight],
  );
  usePageActions(promoteActions);

  // Guard: non-owners see nothing (double safety — mount in MyOrg is already gated)
  if (!canView) return null;

  const owners = members
    .filter(m => m.role === 'ORG_OWNER')
    .sort((a, b) => {
      if (a.discordId === foundingOwnerDiscordId) return -1;
      if (b.discordId === foundingOwnerDiscordId) return 1;
      return (a.joinedAt ?? 0) - (b.joinedAt ?? 0);
    });
  const promotable = members.filter(m => m.role !== 'ORG_OWNER');
  const atCap = owners.length >= OWNER_SEAT_CAP;

  return (
    <Card>
      {/* ── Header ── */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
          <Crown className="h-4 w-4 text-forge-400" strokeWidth={2.5} />
          Owner Board
          {!isLoading && (
            <span className="ml-1 font-mono text-[10px] font-normal text-iron-500">
              {owners.length}/{OWNER_SEAT_CAP}
              {atCap && <span className="ml-1 text-amber-600">(cap)</span>}
            </span>
          )}
        </h2>
      </div>

      {/* ── Add co-owner (founding owner only) ── */}
      {actorIsFoundingOwner && (
        <div className={`${SETTLE} mb-4 space-y-3 border border-forge-900/40 bg-iron-950 p-4`}>
          <p className="font-display text-[9px] font-black uppercase tracking-[0.22em] text-forge-400">
            Add co-owner
          </p>
          {!isLoading && promotable.length === 0 ? (
            <p className="text-[11px] italic text-iron-500">
              No other members yet — add someone via Org Members below, then promote them here.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              <select
                value={addSelectedId}
                onChange={e => { setAddSelectedId(e.target.value); setAddError(''); }}
                disabled={atCap || promoteMut.isPending}
                title={atCap ? 'Owner seats full — max 3' : undefined}
                className="input flex-1 text-sm disabled:opacity-50"
              >
                <option value="">Select a member…</option>
                {promotable.map(m => (
                  <option key={m.discordId} value={m.discordId}>
                    {displayName(m)} · {roleLabel(m.role)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => addSelectedId && promoteMut.mutate(addSelectedId)}
                disabled={!addSelectedId || atCap || promoteMut.isPending}
                title={atCap ? 'Owner seats full — max 3' : undefined}
                className="btn-primary flex items-center gap-1.5 disabled:opacity-50"
              >
                {promoteMut.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <ArrowUpCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
                )}
                Promote to owner
              </button>
            </div>
          )}
          {addError && (
            <div className="flex items-center gap-2 text-xs text-red-300">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
              {addError}
            </div>
          )}
        </div>
      )}

      {/* ── Appoint / propose feedback (dismissible) ── */}
      {appointError && (
        <div className="mb-3 flex items-center gap-2 rounded bg-red-950/50 px-3 py-2 text-xs text-red-300">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
          <span className="flex-1">{appointError}</span>
          <button onClick={() => setAppointError('')} className="ml-2 text-red-500 hover:text-red-300" aria-label="Dismiss error">
            <X className="h-3 w-3" strokeWidth={2.5} />
          </button>
        </div>
      )}
      {proposeError && (
        <div className="mb-3 flex items-center gap-2 rounded bg-red-950/50 px-3 py-2 text-xs text-red-300">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
          <span className="flex-1">{proposeError}</span>
          <button onClick={() => setProposeError('')} className="ml-2 text-red-500 hover:text-red-300" aria-label="Dismiss error">
            <X className="h-3 w-3" strokeWidth={2.5} />
          </button>
        </div>
      )}
      {proposeNotice && (
        <div className="mb-3 flex items-center gap-2 rounded bg-white/[0.05] px-3 py-2 text-xs text-amber-200">
          <Vote className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
          <span className="flex-1">{proposeNotice}</span>
          <button onClick={() => setProposeNotice('')} className="ml-2 text-amber-500 hover:text-amber-300" aria-label="Dismiss">
            <X className="h-3 w-3" strokeWidth={2.5} />
          </button>
        </div>
      )}

      {/* ── Loading state ── */}
      {isLoading && (
        <div className="flex items-center gap-2 py-8 text-iron-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-xs">Loading owners…</span>
        </div>
      )}

      {/* ── Empty state (shouldn't happen — every org has >=1 owner) ── */}
      {!isLoading && owners.length === 0 && (
        <p className="py-6 text-center text-xs text-iron-500">
          <Users className="mr-1 inline h-3.5 w-3.5" strokeWidth={2.5} />
          No owners found.
        </p>
      )}

      {/* ── Owner list ── */}
      {!isLoading && owners.length > 0 && (
        <div className="space-y-1">
          {owners.map((member, i) => {
            const isSelf = member.discordId === currentUserId;
            const isFoundingRow = member.discordId === foundingOwnerDiscordId;
            return (
              <OwnerRow
                key={member.discordId}
                member={member}
                index={i}
                isSelf={isSelf}
                isFoundingRow={isFoundingRow}
                canAppoint={actorIsFoundingOwner && !isFoundingRow}
                canPropose={actorCanPropose && !isFoundingRow && !isSelf}
                onAppointRequest={setAppointTarget}
                onProposeRequest={setProposeTarget}
              />
            );
          })}
        </div>
      )}

      {/* ── Appoint founding owner modal ── */}
      {appointTarget && (
        <ConfirmModal
          tone="warn"
          icon={Crown}
          title={`Make ${displayName(appointTarget)} the founding owner?`}
          body="They gain the power to add co-owners and appoint a successor. You remain an Owner — this does not demote you."
          confirmLabel="Appoint"
          confirmIcon={Crown}
          pending={appointMut.isPending}
          onConfirm={() => appointMut.mutate(appointTarget.discordId)}
          onCancel={() => setAppointTarget(null)}
        />
      )}

      {/* ── Propose removal modal ── */}
      {proposeTarget && (
        <ConfirmModal
          tone="danger"
          icon={Vote}
          title={`Propose removing ${displayName(proposeTarget)} as an owner?`}
          body="This opens a governance vote. Every current owner gets to vote — majority passes it (see Governance Proposals below)."
          confirmLabel="Open vote"
          confirmIcon={Vote}
          pending={proposeMut.isPending}
          onConfirm={() => proposeMut.mutate(proposeTarget.discordId)}
          onCancel={() => setProposeTarget(null)}
        />
      )}
    </Card>
  );
}
